"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { useAuth } from "@/core/auth/use-auth";
import { normalizeAppError } from "@/shared/lib/async-result";
import { SESSION_TYPE_OPTIONS } from "../../../constants/session-types";
import { createPaymentOrder } from "../../../data/repositories/booking.repository";
import { useBookingStepperStore } from "../../states/booking-stepper.store";
import { useRazorpayCheckout } from "../../hooks/use-razorpay-checkout";
import { MODE_LABEL } from "./shared";
function formatSlotDate(iso: string, locale: AppLocale): string {
  try {
    return new Intl.DateTimeFormat(locale === "hi" ? "hi-IN" : "en-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
function getErrorMessage(error: unknown): string {
  return normalizeAppError(error).message;
}
export function Step5ReviewPay({
  changeHealerHref,
  confirmationHrefForBooking,
  healerName,
  healerSlug,
  locale
}: {
  changeHealerHref?: string;
  confirmationHrefForBooking?: (bookingId: string) => string;
  healerName: string;
  healerSlug: string;
  locale: AppLocale;
}) {
  const isHindi = locale === "hi";
  const router = useRouter();
  const {
    user
  } = useAuth();
  const {
    openCheckout
  } = useRazorpayCheckout();
  const sessionType = useBookingStepperStore(state => state.sessionType);
  const selectedMode = useBookingStepperStore(state_0 => state_0.selectedMode);
  const selectedOption = useBookingStepperStore(state_1 => state_1.selectedOption);
  const selectedSlot = useBookingStepperStore(state_2 => state_2.selectedSlot);
  const holdId = useBookingStepperStore(state_3 => state_3.holdId);
  const goToStep = useBookingStepperStore(state_4 => state_4.goToStep);
  const [isProcessing, setIsProcessing] = useState(false);
  const sessionTypeLabel = SESSION_TYPE_OPTIONS.find(option => option.value === sessionType);
  async function handleConfirmAndPay() {
    if (!holdId) {
      toast.error(isHindi ? "स्लॉट होल्ड नहीं मिला। कृपया फिर से समय चुनें।" : "No active slot hold found. Please choose a time again.");
      return;
    }
    setIsProcessing(true);
    try {
      const order = await createPaymentOrder(holdId);
      await openCheckout({
        key: order.keyId,
        // order.amount is already in the smallest currency unit (paise) —
        // the backend computes Math.round(price * 100) when creating the
        // Razorpay order, so it must be passed through unchanged here.
        amount: order.amount,
        currency: order.currency,
        order_id: order.razorpayOrderId,
        name: "ItsHealer",
        description: isHindi ? `${healerName} के साथ सत्र` : `Session with ${healerName}`,
        prefill: {
          name: user?.name,
          email: user?.email
        },
        theme: {
          color: "#155A7D"
        },
        handler: () => {
          // Do NOT trust this callback to confirm the booking — the actual
          // confirmation only happens via a server-side Razorpay webhook.
          // Just redirect to the confirmation page, which polls booking
          // status until the webhook has landed.
          router.push(confirmationHrefForBooking?.(order.booking.id) ?? localizeHref(locale, `/bookings/${order.booking.id}/confirmation`));
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
            toast(isHindi ? "भुगतान रद्द कर दिया गया। आप फिर से प्रयास कर सकते हैं।" : "Payment was cancelled. You can try again.");
          }
        }
      });
    } catch (caughtError) {
      toast.error(getErrorMessage(caughtError));
      setIsProcessing(false);
    }
  }
  return <div>
      <h2 className="font-heading text-xl font-semibold text-[#0E2A3A]">
        {isHindi ? "समीक्षा करें और भुगतान करें" : "Review & pay"}
      </h2>

      <div className="mt-6 space-y-4 rounded-2xl bg-[#FAF8F5] p-5 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-500">{isHindi ? "हीलर" : "Healer"}</span>
          <span className="font-semibold text-[#0E2A3A]">{healerName}</span>
        </div>
        {sessionTypeLabel && <div className="flex justify-between">
            <span className="text-slate-500">{isHindi ? "सत्र प्रकार" : "Session type"}</span>
            <span className="font-medium text-slate-700">
              {isHindi ? sessionTypeLabel.hi : sessionTypeLabel.en}
            </span>
          </div>}
        {selectedSlot && <div className="flex justify-between">
            <span className="text-slate-500">{isHindi ? "समय" : "When"}</span>
            <span className="font-medium text-slate-700">
              {formatSlotDate(selectedSlot.start, locale)}
            </span>
          </div>}
        {selectedMode && <div className="flex justify-between">
            <span className="text-slate-500">{isHindi ? "मोड" : "Mode"}</span>
            <span className="font-medium text-slate-700">
              {isHindi ? MODE_LABEL[selectedMode].hi : MODE_LABEL[selectedMode].en}
            </span>
          </div>}
        {selectedOption && <div className="flex justify-between">
            <span className="text-slate-500">{isHindi ? "अवधि" : "Duration"}</span>
            <span className="font-medium text-slate-700">
              {selectedOption.durationMinutes} {isHindi ? "मिनट" : "min"}
            </span>
          </div>}

        <div className="border-t border-[#EAE5DA] pt-4">
          <div className="flex justify-between text-base font-semibold text-[#0E2A3A]">
            <span>{isHindi ? "कुल राशि" : "Total"}</span>
            <span>₹{selectedOption?.price ?? selectedSlot?.price ?? 0}</span>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            {isHindi ? "प्लेटफ़ॉर्म शुल्क/छूट लागू नहीं है।" : "No additional platform fees or discounts apply."}
          </p>
        </div>
      </div>

      <p className="mt-4 text-xs leading-5 text-slate-500">
        {isHindi ? "रद्दीकरण नीति: सत्र शुरू होने से 24 घंटे से अधिक समय पहले रद्द करने पर पूर्ण धनवापसी मिलती है। इसके बाद की रद्दीकरण नीति के लिए समर्थन से संपर्क करें।" : "Cancellation policy: cancellations made more than 24 hours before the session start are fully refundable. Contact support for cancellations after that window."}
      </p>

      <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <button type="button" onClick={() => goToStep(3)} className="rounded-full border border-[#EAE5DA] px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
          {isHindi ? "समय बदलें" : "Change Slot"}
        </button>
        <a href={changeHealerHref ?? localizeHref(locale, `/healers/${healerSlug}`)} className="flex items-center justify-center rounded-full border border-[#EAE5DA] px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
          {isHindi ? "हीलर बदलें" : "Change Healer"}
        </a>
        <button type="button" disabled={isProcessing} onClick={handleConfirmAndPay} className="flex items-center justify-center gap-2 rounded-full bg-[#C06A42] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#A55332] disabled:cursor-not-allowed disabled:opacity-60">
          {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
          {isHindi ? "पुष्टि करें और भुगतान करें" : "Confirm & Pay"}
        </button>
      </div>
    </div>;
}